<template>
  <div class="mt-2">
    <div class="d-flex flex-column ga-12">
      <!-- Simple alerts -->
      <Alert
        v-for="(alert, i) in alertsToDisplay"
        v-bind="alert"
        in-page
        :key="`headAlert${i}`"
      />

      <!-- Complex alerts -->
      <Alert
        v-if="!isArchived && isSubmittedAndDeclined"
        in-page
        type="error"
        icon="mdi-alert-circle"
        background-colour
        data-testid="submittedAndDeclinedAlert"
        :text="`${getValue(
          'student.firstName'
        )}'s parent/carer has declined your school's offer after submitting an enrolment application`"
      >
        <template #optional>
          <div class="pt-4 alert--textsimple">
            <p :class="{ 'mb-0': isArchived || !getValue('applicationID') }">
              Please withdraw {{ getValue('student.firstName') }}'s enrolment
              application
            </p>
            <p class="mb-0" v-if="!isArchived && getValue('applicationID')">
              <strong
                ><router-link :to="{ name: 'ShsOfferApplicationView' }">
                  Go to enrolment application
                </router-link></strong
              >
            </p>
          </div>
        </template>
      </Alert>

      <Alert
        v-if="
          !isArchived &&
          (isSubmittedByPaperAndDeclined || isInternalTransferAndDeclined)
        "
        in-page
        type="error"
        icon="mdi-alert-circle"
        background-colour
        :data-testid="`${
          isSubmittedByPaperAndDeclined
            ? 'submittedByPaperAndDeclinedAlert'
            : 'internalTransferAndDeclinedAlert'
        }`"
        text="This enrolment application is invalid and should be withdrawn."
      >
        <template #optional>
          <div class="pt-4 alert--textsimple">
            <p>
              {{ getValue('student.firstName') }}’s parent/carer has declined
              your selective high school offer{{
                isSubmittedByPaperAndDeclined
                  ? ' after submitting a paper enrolment application'
                  : ''
              }}.
            </p>
            <p>
              <strong>Step 1: </strong>Go to ERN and update the student record
            </p>
            <p class="mb-0">
              <strong
                >Step 2:
                <a href="#" @click.prevent="showWithdrawConfirmModal"
                  >Mark enrolment application status as 'withdrawn'</a
                >
              </strong>
            </p>
          </div>
        </template>
      </Alert>

      <Alert
        v-if="isSubmittedAndAccepted"
        in-page
        type="success"
        icon="mdi-check-circle"
        :class="{ 'mb-8': isArchived }"
        data-testid="submittedAndAcceptedAlert"
        :text="`${getValue('student.firstName')} ${getValue(
          'student.familyName'
        )}'s parent/carer(s) have submitted an enrolment application`"
      >
        <template #optional>
          <div class="pt-4 alert--textsimple">
            <p class="mb-0" v-if="getValue('applicationID') && !isArchived">
              <strong
                ><router-link :to="{ name: 'ShsOfferApplicationView' }">
                  Go to enrolment application
                </router-link></strong
              >
            </p>
          </div>
        </template>
      </Alert>

      <Alert
        v-if="isWithdrawnAndDeclined && !isWithdrawnAndDeclinedInternalTransfer"
        in-page
        icon="mdi-lock-outline"
        class="greyBorderAndIcon"
        :class="{ 'mb-8': isArchived }"
        data-testid="withdrawnAndDeclinedAlert"
        :text="`The parent/carer initially accepted your school's offer and submitted ${
          getValue('applicationID') ? 'an' : 'a paper'
        } enrolment application, but then declined.`"
      >
        <template #optional>
          <div class="alert--textsimple">
            <p
              v-if="getValue('applicationID')"
              :class="{ 'mb-0': !getValue('applicationID') || isArchived }"
            >
              Enrolment application withdrawn.
            </p>
            <p
              v-else
              :class="{ 'mb-0': !getValue('applicationID') || isArchived }"
            >
              Enrolment application recorded as manually withdrawn in ERN.
            </p>
            <p class="mb-0" v-if="getValue('applicationID') && !isArchived">
              <strong
                ><router-link :to="{ name: 'ShsOfferApplicationView' }">
                  Go to enrolment application
                </router-link></strong
              >
            </p>
          </div>
        </template>
      </Alert>

      <Alert
        v-if="isInvalidAndDeclined"
        in-page
        icon="mdi-lock-outline"
        class="greyBorderAndIcon"
        :class="{ 'mb-8': isArchived }"
        data-testid="invalidAndDeclinedAlert"
        :text="`${getValue(
          'student.firstName'
        )}'s parent/carer has declined your school's offer after submitting an enrolment application`"
      >
        <template #optional>
          <div class="alert--textsimple">
            <p :class="{ 'mb-0': isArchived || !getValue('applicationID') }">
              {{ getValue('student.firstName') }}'s enrolment application has
              been marked as invalid.
            </p>
            <p class="mb-0" v-if="getValue('applicationID') && !isArchived">
              <strong
                ><router-link :to="{ name: 'ShsOfferApplicationView' }">
                  Go to enrolment application
                </router-link></strong
              >
            </p>
          </div>
        </template>
      </Alert>

      <Alert
        v-if="displayCensusSchoolWithdrawnAlert"
        in-page
        type="error"
        icon="mdi-alert-circle"
        background-colour
        data-testid="shared-application-withdrawn-alert"
        text="Enrolment application withdrawn"
      >
        <template #optional>
          <div class="alert--textsimple">
            <p
              class="pt-4"
              v-text="
                `${matchedApplication.student.firstName}'s enrolment application has been withdrawn at census school
${matchedApplication.schoolName}. Please unmatch ${matchedApplication.student.firstName}'s selective high school offer so it can be matched with an active
enrolment application at a census school.`
              "
            />

            <p class="mb-0">
              <strong>
                <a
                  href="#"
                  @click.prevent="scrollToMatches"
                  class="text-uppercase"
                  v-text="'Jump to the list of matches'"
                />
              </strong>
            </p>
          </div>
        </template>
      </Alert>

      <Alert
        v-if="displayMatchedOfferAlert"
        in-page
        type="success"
        icon="mdi-check-circle"
        :class="{ 'mb-8': isArchived }"
        data-testid="matched-success-alert"
        text="Matched offer"
      >
        <template #optional>
          <div class="pt-4 alert--textsimple">
            <p
              v-text="
                `This offer has been matched with an enrolment application at census school ${matchedApplication.schoolName}.`
              "
            />
            <p class="font-weight-bold text-uppercase mb-0">
              <router-link :to="{ name: 'ShsOfferApplicationView' }">
                Go to enrolment application
              </router-link>
            </p>
          </div>
        </template>
      </Alert>

      <Alert
        v-if="displayUnmatchedOfferAlert"
        in-page
        type="info"
        data-testid="unmatchedOfferAlert"
        text="Unmatched offer"
      >
        <template #optional>
          <div class="alert--textsimple">
            <p class="pt-4">
              This offer needs to be matched with an enrolment application at a
              census school.
            </p>
            <p class="mb-0">
              <strong>
                <a
                  href="#"
                  @click.prevent="scrollToMatches"
                  class="text-uppercase"
                  v-text="'Jump to the list of matches'"
                />
              </strong>
            </p>
          </div>
        </template>
      </Alert>
    </div>

    <!-- end top alerts -->

    <div
      v-if="showMoreActionsBtn"
      class="d-flex justify-end mt-6"
      data-testid="shs-more-actions-btn"
    >
      <v-menu offset-y left>
        <template #activator="{ on, attrs }">
          <AdsButton
            secondary
            v-on="on"
            v-bind="attrs"
            button-text="More actions"
            icon="more_vert"
            :disabled="isMoreActionsButtonDisabled"
            data-testid="moreActionsButton"
          />
        </template>
        <v-list>
          <v-list-item
            v-for="({ click, title, icon, ...btn }, index) in buttonActions"
            :key="index"
            @click="click"
            v-bind="btn"
          >
            <v-list-item-icon>
              <v-icon>{{ icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ title }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div v-if="renderReady" :class="['pb-6', { 'mt-8': !showMoreActionsBtn }]">
      <AdsExpansionPanel
        :items="cExpansionPanelItems"
        v-model="openPanels"
        data-testid="sdu-accordion"
        class="mt-3"
      >
        <template v-for="cps in cPanelsData" #[`content${cps.id}`]>
          <template
            v-for="({ invisibleFieldOnEmpty, ...cpd }, f) in cps.sections"
          >
            <ReadOnlyFieldList
              :key="`cpd${cps.id}${f}`"
              :item-object="cpd.fields"
              :data-testid="`rofl${cpd.id}`"
              :columns="cps.columns"
              :title="cpd.name"
              v-bind="{ invisibleFieldOnEmpty }"
            />
          </template>
        </template>
      </AdsExpansionPanel>
    </div>
    <div class="mb-12">
      <virtual-selective-matches
        v-if="showVirtualApplicationMatches"
        ref="virtual-selective-matches"
        :items="virtualSelectiveMatchItems"
        :is-matched="isMatched"
        :is-unmatched="isUnmatched"
        data-testid="enrolment-application-matches"
        class="mt-9 mb-12"
        @reloadShsOffer="reloadShsOffer"
        @matchSharedEnrolment="handleMatchSharedEnrolment"
        @unmatchSharedEnrolment="handleUnmatchSharedEnrolment"
      />
    </div>

    <AppModal
      v-if="isShowConfirmSubmittedByPaperModal"
      ref="confirmSubmittedByPaperModal"
      data-testid="confirm-submitted-by-paper-modal"
    >
      <template slot="header">
        <div class="d-flex justify-space-between pl-3 w-full">
          <span class="font-weight-bold" role="alert"> Confirm update </span>
          <v-icon
            @click="isShowConfirmSubmittedByPaperModal = false"
            class="closeBtn"
          >
            mdi-close
          </v-icon>
        </div>
      </template>
      <template slot="body">
        <div class="content mt-3 mx-3">
          <p>
            <strong>
              {{ getValue('student.firstName') }}
              {{ getValue('student.familyName') }}'s enrolment application
              status will be marked as 'submitted by paper'.</strong
            >
          </p>
          <p>Please ensure the paper forms have been entered in ERN.</p>
          <Alert
            in-page
            icon="mdi-information"
            class="in-page-alert--small"
            alert-class="alert--no-border"
            elevation="0"
            background-colour
            type="info"
          >
            <template #optional>
              <p>This action cannot be undone.</p>
              <p>
                The parent/carer <em>will not</em> be sent an email to inform
                them of this update.
              </p>
            </template>
          </Alert>
        </div>
      </template>
      <template #footer>
        <div class="mb-0 mr-3 pb-2">
          <AdsButton
            icon="mdi-check"
            button-text="Confirm"
            aria-label="Confirm"
            @click="handleSubmittedByPaperConfirm"
          />
          <AdsButton
            tertiary
            button-text="Cancel"
            aria-label="Cancel"
            @click="isShowConfirmSubmittedByPaperModal = false"
          />
        </div>
      </template>
    </AppModal>
    <AppModal
      v-if="isShowConfirmWithdrawModal"
      ref="confirmWithdrawModal"
      data-testid="confirm-withdraw-modal"
    >
      <template slot="header">
        <div class="d-flex justify-space-between pt-3 pl-3 w-full">
          <span class="font-weight-bold">
            Mark enrolment application as 'Withdrawn'
          </span>
          <v-icon
            @click="isShowConfirmWithdrawModal = false"
            class="closeBtn"
            aria-label="close"
          >
            mdi-close
          </v-icon>
        </div>
      </template>
      <template slot="body">
        <div class="content mx-3">
          <p class="mb-3">
            {{ getValue('student.firstName') }}
            {{ getValue('student.familyName') }}'s enrolment application will be
            marked as <strong>'Withdrawn'.</strong>
          </p>
          <Alert
            in-page
            icon="mdi-alert"
            class="pt-3 modal-alerts in-page-alert--small"
            alert-class="alert--no-border"
            elevation="0"
            background-colour
            type="warning"
            role="none"
          >
            <template #optional>
              <p>
                Please ensure that {{ getValue('student.firstName') }}
                {{ getValue('student.familyName') }}'s student record has been
                updated in ERN before proceeding.
              </p>
              <p>This action cannot be undone.</p>
            </template>
          </Alert>
        </div>
      </template>
      <template #footer>
        <div class="mb-0 mr-3 pb-2">
          <AdsButton
            class="button red darken-1"
            button-text="Mark as withdrawn"
            aria-label="Mark as withdrawn"
            @click="handleWithdrawConfirm"
          />
          <AdsButton
            tertiary
            button-text="Cancel"
            aria-label="Cancel"
            @click="isShowConfirmWithdrawModal = false"
          />
        </div>
      </template>
    </AppModal>
  </div>
</template>

<script>
// @ts-check
import fieldHelperMixin from '@/helpers/fieldHelperMixin'
import ReadOnlyFieldList from '@/components/application/ReadOnlyFieldList.vue'
import {
  STREAM,
  DATASET,
  SHS_STATUS,
  CONTACT_PHONE_TYPE,
  SHS_OFFER_STATUS,
  SCHOOL_SELECTIVENESS,
  VIRTUAL_SELECTIVE_MATCHING
} from '@/constants'
import { AdsExpansionPanel, Alert, AdsButton } from '@nswdoe/doe-ui-core'
import { mapActions, mapGetters } from 'vuex'
import formatters from '@/helpers/formatters'
import AppModal from '@/components/app/AppModal.vue'
import VirtualSelectiveMatches from '@/components/application/VirtualSelectiveMatches.vue'
import {
  stringEqualsCaseInsensitive,
  stringIncludesCaseInsensitive
} from '@/helpers/generalUtils.js'

/**
 * @typedef {import('../../types/VirtualSelective.d.ts').MatchListItem} MatchListItem
 */

export default {
  name: 'ShsSummary',
  mixins: [fieldHelperMixin],
  components: {
    AdsExpansionPanel,
    ReadOnlyFieldList,
    Alert,
    AdsButton,
    AppModal,
    VirtualSelectiveMatches
  },
  props: {
    schoolCode: {
      type: String
    },
    isArchived: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      openPanels: !this.isArchived && 0,
      showChangeStatus: false,
      addressFormatter: () => {},
      isShowConfirmSubmittedByPaperModal: false,
      isShowConfirmWithdrawModal: false
    }
  },
  watch: {
    referenceData: {
      handler(v) {
        this.setAddressFormatter(v)
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters([
      'currentStream',
      'lookupCode',
      'referenceData',
      'isVirtualSelectiveSchool',
      'selectedSchool'
    ]),
    state() {
      return this.$store.state
    },
    showMoreActionsBtn() {
      if (this.isVirtualSelectiveSchool || this.isArchived) {
        return false
      }
      return true
    },
    cPanelsData() {
      const parentCarer = this.parentCarer || {}
      const [contact] = parentCarer?.contactDetails || [{}]

      const baseOfferData = [
        {
          name: 'Student details',
          id: 'StudentDetails',
          columns: 3,
          sections: [
            {
              invisibleFieldOnEmpty: false,
              fields: {
                'First name': this.getValue('student.firstName'),
                'Preferred name': this.getValue('student.prefFirstName'),
                'Family name': this.getValue('student.familyName'),
                Gender: formatters.gender(this.getValue('student.genderCode')),
                'Date of birth': formatters.displayDate(
                  this.getValue('student.dateOfBirth')
                ),
                'Residential address': this.addressFormatter({
                  ...this.getValue('residentialAddress')
                })
              }
            },
            {
              name: 'Residency status',
              invisibleFieldOnEmpty: false,
              fields: {
                'Student residency status':
                  this.application?.student?.residencyStatus,
                'Student visa class': this.application?.student?.visaClass,
                'Student visa sub-class':
                  this.application?.student?.visaSubClass,
                'Aboriginal or Torres Strait Islander origin': this.application
                  ?.student?.isAboriTorStraitIslander
                  ? 'Yes'
                  : 'No'
              }
            }
          ]
        },
        ...(parentCarer.parentCarerCompletingApp === true
          ? [
              {
                name: 'Parent/carer details',
                id: 'ParentCarerDetails',
                columns: 3,
                sections: [
                  {
                    invisibleFieldOnEmpty: false,
                    fields: {
                      Title: parentCarer?.parentCarerTitle,
                      'Given name': parentCarer.parentCarerGivenName,
                      'Family name': parentCarer.parentCarerFamilyName,
                      Relationship: this.lookupCode(
                        DATASET.RELATIONSHIP,
                        parentCarer.parentCarerRelation
                      ),
                      'Residential address': this.addressFormatter(
                        parentCarer.residentialAddress
                      )
                    }
                  },
                  {
                    name: 'Contact details',
                    invisibleFieldOnEmpty: false,
                    fields: {
                      'Contact email': this.parentCarer?.parentCarerEmail,
                      'Phone number': contact.contactValue,
                      'Phone number type':
                        CONTACT_PHONE_TYPE[contact.contactType] || ''
                    }
                  }
                ]
              }
            ]
          : [])
      ]
      return baseOfferData
    },

    /**
     * @returns MatchListItem[]
     */
    virtualSelectiveMatchItems() {
      if (this.isMatched) {
        return [this.getValue('matchedAuroraCensusEnrolment')]
      }
      if (this.isUnmatched) {
        return this.getValue('auroraCensusEnrolmentList') ?? []
      }
      return []
    },

    /**
     * @returns MatchListItem
     */
    matchedApplication() {
      if (this.isMatched && this.virtualSelectiveMatchItems.length === 1) {
        return this.virtualSelectiveMatchItems[0]
      }
      return null
    },
    isSelectedSchoolPartiallySelective() {
      return (
        this.selectedSchool?.selectiveInd === SCHOOL_SELECTIVENESS.PARTIALLY
      )
    },
    cExpansionPanelItems() {
      return this.cPanelsData.map(({ name, id }) => ({
        id,
        title: name
      }))
    },
    parentCarer() {
      return this.getValue('parentCarers').find(
        (pc) => pc.parentCarerCompletingApp === true
      )
    },
    buttonActions() {
      const internalTransferItem = {
        title: 'Mark as internal transfer',
        icon: 'mdi-cached',
        click: () => {
          this.openInternalTransferModal()
        }
      }
      return [
        {
          title: 'Mark as submitted by paper form',
          icon: 'mdi-check',
          click: () => {
            this.isShowConfirmSubmittedByPaperModal = true
          }
        },
        ...(this.isSelectedSchoolPartiallySelective
          ? [internalTransferItem]
          : [])
      ]
    },
    isMoreActionsButtonDisabled() {
      return !(
        this.isOfferAccepted &&
        stringEqualsCaseInsensitive(
          this.getValue('applicationStatus'),
          SHS_STATUS.ISSUED
        )
      )
    },
    applicationHistory() {
      return this.application?.applicationHistory
    },
    isMatched() {
      return stringEqualsCaseInsensitive(
        this.getValue('applicationStatus'),
        VIRTUAL_SELECTIVE_MATCHING.MATCHED
      )
    },
    isUnmatched() {
      return stringEqualsCaseInsensitive(
        this.getValue('applicationStatus'),
        VIRTUAL_SELECTIVE_MATCHING.UNMATCHED
      )
    },
    isOfferAccepted() {
      return stringEqualsCaseInsensitive(
        this.getValue('offerStatus'),
        SHS_OFFER_STATUS.ACCEPTED
      )
    },
    isOfferNa() {
      return stringEqualsCaseInsensitive(
        this.getValue('applicationStatus'),
        SHS_STATUS.NA
      )
    },
    isOfferDeclined() {
      return stringEqualsCaseInsensitive(
        this.getValue('offerStatus'),
        SHS_OFFER_STATUS.DECLINED
      )
    },
    isSubmittedAndAccepted() {
      return (
        stringEqualsCaseInsensitive(
          this.getValue('applicationStatus'),
          SHS_STATUS.SUBMITTED
        ) && this.isOfferAccepted
      )
    },
    showVirtualApplicationMatches() {
      if (this.isArchived) {
        return false
      }
      if (!this.isVirtualSelectiveSchool) {
        return false
      }

      if (this.isOfferDeclined && !this.isMatched) {
        return false
      }

      return true
    },
    isMatchedAndSharedApplicationWithdrawn() {
      let offerWithdrawn = false
      if (
        stringIncludesCaseInsensitive(
          this.getValue('censusEnrolmentStatus'),
          SHS_STATUS.WITHDRAWN
        )
      ) {
        offerWithdrawn = true
      }
      if (
        stringIncludesCaseInsensitive(
          this.getValue('censusERNStatus'),
          SHS_STATUS.WITHDRAWN
        )
      ) {
        offerWithdrawn = true
      }

      return (
        stringEqualsCaseInsensitive(
          this.getValue('applicationStatus'),
          VIRTUAL_SELECTIVE_MATCHING.MATCHED
        ) && offerWithdrawn
      )
    },
    isSubmittedAndDeclined() {
      return (
        stringEqualsCaseInsensitive(
          this.getValue('applicationStatus'),
          SHS_STATUS.SUBMITTED
        ) && this.isOfferDeclined
      )
    },
    isSubmittedByPaperAndAccepted() {
      return (
        stringEqualsCaseInsensitive(
          this.getValue('applicationStatus'),
          SHS_STATUS.SUBMITTED_BY_PAPER
        ) && this.isOfferAccepted
      )
    },
    isSubmittedByPaperAndDeclined() {
      return (
        stringEqualsCaseInsensitive(
          this.getValue('applicationStatus'),
          SHS_STATUS.SUBMITTED_BY_PAPER
        ) && this.isOfferDeclined
      )
    },
    isInternalTransferAndAccepted() {
      return (
        stringEqualsCaseInsensitive(
          this.getValue('applicationStatus'),
          SHS_STATUS.INTERNAL_TRANSFER
        ) && this.isOfferAccepted
      )
    },
    isInternalTransferAndDeclined() {
      return (
        stringEqualsCaseInsensitive(
          this.getValue('applicationStatus'),
          SHS_STATUS.INTERNAL_TRANSFER
        ) && this.isOfferDeclined
      )
    },
    isWithdrawnAndDeclined() {
      return (
        stringEqualsCaseInsensitive(
          this.getValue('applicationStatus'),
          SHS_STATUS.WITHDRAWN
        ) && this.isOfferDeclined
      )
    },
    isWithdrawnAndDeclinedInternalTransfer() {
      const lastUpdatedRecord = this.applicationHistory?.at(-2)
      if (!lastUpdatedRecord) {
        return false
      }
      return (
        stringEqualsCaseInsensitive(
          lastUpdatedRecord.enrolmentStatus,
          SHS_STATUS.INTERNAL_TRANSFER
        ) &&
        stringEqualsCaseInsensitive(
          this.getValue('applicationStatus'),
          SHS_STATUS.WITHDRAWN
        ) &&
        this.isOfferDeclined
      )
    },
    isInvalidAndDeclined() {
      return (
        stringEqualsCaseInsensitive(
          this.getValue('applicationStatus'),
          SHS_STATUS.INVALID
        ) && this.isOfferDeclined
      )
    },
    renderReady() {
      return Boolean(this.addressFormatter)
    },
    alertsToDisplay() {
      return [
        // if application is issued status
        ...(stringEqualsCaseInsensitive(
          this.getValue('applicationStatus'),
          SHS_STATUS.ISSUED
        )
          ? [
              {
                type: 'info',
                text: 'The parent/carer has been issued instructions to enrol online',
                class: { 'mb-8': this.isArchived },
                'data-testid': 'issuedAlert'
              }
            ]
          : []),
        // if application is issued status
        ...(stringEqualsCaseInsensitive(
          this.getValue('applicationStatus'),
          SHS_STATUS.AUTO_RETRACTED
        )
          ? [
              {
                icon: 'mdi-lock-outline',
                class: ['greyBorderAndIcon', { 'mb-8': this.isArchived }],
                text: "The parent/carer initially accepted your school's offer, but then declined.",
                subtext: 'Enrolment application auto retracted.',
                'data-testid': 'autoRetractedAlert'
              }
            ]
          : []),

        // if application is submitted by paper status
        ...(this.isSubmittedByPaperAndAccepted
          ? [
              {
                text: `${this.getValue('student.firstName')} ${this.getValue(
                  'student.familyName'
                )}'s parent/carer(s) have submitted a paper enrolment application`,
                class: { 'mb-8': this.isArchived },
                type: 'success',
                'data-testid': 'submittedByPaperAcceptedAlert'
              }
            ]
          : []),
        // if application is Internal transfer status
        ...(this.isInternalTransferAndAccepted
          ? [
              {
                icon: 'mdi-lock-outline',
                class: ['greyBorderAndIcon', { 'mb-8': this.isArchived }],
                text: 'Selective enrolment application marked as internal transfer',
                subtext: `${this.getValue('student.firstName')} ${this.getValue(
                  'student.familyName'
                )}'s parent/carer(s) have submitted a separate mainstream enrolment application, which has been internally transferred to a selective high school enrolment in ERN.`,
                'data-testid': 'internalTransferAndAcceptedAlert'
              }
            ]
          : []),
        // if application is Internal transfer status
        ...(this.isWithdrawnAndDeclinedInternalTransfer
          ? [
              {
                icon: 'mdi-lock-outline',
                class: ['greyBorderAndIcon', { 'mb-8': this.isArchived }],
                text: `The parent/carer initially accepted your school's offer and submitted an application, but then declined.`,
                subtext: `Enrolment application recorded as manually withdrawn in ERN.`,
                'data-testid': 'withdrawnAndDeclinedAlert'
              }
            ]
          : []),
        // if application is declined and unmatched
        ...(this.isOfferDeclined && this.isOfferNa
          ? [
              {
                icon: 'mdi-lock-outline',
                class: ['greyBorderAndIcon', { 'mb-8': this.isArchived }],
                text: `Offer declined by parent/carer`,
                subtext: `${
                  this.isArchived
                    ? ``
                    : `${this.getValue(
                        'student.firstName'
                      )}'s parent/carer has declined your school's offer. If an enrolment application was matched, it has now been unmatched
and read-only access has been revoked.`
                }`,
                'data-testid': 'declined-and-na'
              }
            ]
          : [])
      ]
    },
    displayMatchedOfferAlert() {
      if (
        this.isArchived ||
        !this.isVirtualSelectiveSchool ||
        !this.isMatched
      ) {
        return false
      }
      return true
    },
    displayUnmatchedOfferAlert() {
      if (
        this.isArchived ||
        !this.isVirtualSelectiveSchool ||
        !this.isUnmatched ||
        !this.isOfferAccepted
      ) {
        return false
      }
      return true
    },
    displayCensusSchoolWithdrawnAlert() {
      if (
        this.isArchived ||
        !this.isVirtualSelectiveSchool ||
        !this.isMatchedAndSharedApplicationWithdrawn
      ) {
        return false
      }
      return true
    }
  },
  methods: {
    ...mapActions(['matchVirtualSelectiveApplication']),
    setAddressFormatter(v) {
      this.addressFormatter = formatters.getAddressFormatter(
        v(DATASET.COUNTRIES),
        v(DATASET.STATES)
      )
    },
    handleSubmittedByPaperConfirm() {
      if (this.isArchived) return
      this.$store
        .dispatch('updateShsOfferEnrolmentStatus', {
          shsOffer: this.application,
          payload: {
            status: 'Submitted By Paper'
          }
        })
        .finally(() => {
          this.isShowConfirmSubmittedByPaperModal = false
        })
    },
    showWithdrawConfirmModal() {
      if (this.isArchived) return
      this.isShowConfirmWithdrawModal = true
    },
    handleWithdrawConfirm() {
      if (this.isArchived) return
      if (this.getValue('applicationID')) {
        const applicationStatusId = 4
        this.$store
          .dispatch('saveCoreApplicationStatus', {
            applicationStatusId
          })
          .then(this.updateWithdrawEnrolmentStatus)
          .catch(() => {
            this.isShowConfirmWithdrawModal = false
          })
      } else {
        this.updateWithdrawEnrolmentStatus()
      }
    },
    async handleUnmatchSharedEnrolment({ applicationID }) {
      await this.matchVirtualSelectiveApplication({
        payload: { applicationID, match: false },
        shsOffer: this.application
      })
      this.reloadShsOffer()
    },
    /**
     * @param {MatchListItem} match
     * @returns {Promise<void>}
     */
    async handleMatchSharedEnrolment(match) {
      const { applicationID } = match

      await this.matchVirtualSelectiveApplication({
        payload: { applicationID },
        shsOffer: this.application
      })
      this.reloadShsOffer()
    },
    reloadShsOffer() {
      this.$store.dispatch('getApplication', [
        this.getValue('shsOfferId'),
        this.selectedSchool?.schoolCode || ''
      ])
    },
    scrollToMatches() {
      window.scrollTo({
        top: this.$refs['virtual-selective-matches']?.$el?.offsetTop,
        behavior: 'smooth'
      })
    },
    updateWithdrawEnrolmentStatus() {
      if (this.isArchived) return
      this.$store
        .dispatch('updateShsOfferEnrolmentStatus', {
          shsOffer: this.application,
          payload: {
            status: 'Withdrawn'
          }
        })
        .finally(() => {
          this.isShowConfirmWithdrawModal = false
        })
    },
    async openInternalTransferModal() {
      if (this.isArchived) return
      await this.$store.dispatch('set', [
        'actionFromStream',
        STREAM.SELECTIVE_OFFERS
      ])
      this.$store.dispatch('set', ['openInternalTransfer', true])
    }
  }
}
</script>
<style lang="scss" scoped>
.w-full {
  width: 100%;
}
::v-deep .alert--element {
  .v-alert {
    margin-bottom: 0;
  }
}
.ga-12 {
  gap: 3rem;
}

::v-deep .alert--element:not(.in-page-alert--small) .v-alert__wrapper {
  margin: 5px 18px;
}
::v-deep .alert--element .v-alert.alert--no-border .v-alert__border {
  display: none;
}
::v-deep .alert--element.in-page-alert--small {
  .v-alert {
    margin-bottom: 0;
  }
  .v-alert__icon.v-icon {
    font-size: 22px;
    height: 22px;
    min-width: 22px;
  }
  p:last-of-type {
    margin-bottom: 0;
  }
}
.greyBorderAndIcon {
  ::v-deep .v-alert__border {
    background-color: $color-placeholder !important;
    border-color: $color-placeholder !important;
  }
  ::v-deep .v-icon {
    color: $color-placeholder !important;
  }
  ::v-deep .alert--textsimple {
    padding-top: 16px !important;
  }
}
.theme--light.v-icon.v-icon--link:focus {
  border: none !important;
  outline: 3px solid currentColor;
}
::v-deep .modal-alerts {
  .alertElement:focus {
    border: none !important;
    outline: 3px solid currentColor;
  }
}
</style>
